import { Box, Flex, HStack, Heading, Icon, Stack, Text } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiFile } from "react-icons/fi";
import { StatusAccentBar } from "../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../common/components/StatusDotLabel";
import { type StatusVariant } from "../../../common/components/StatusVariant";
import { TipTap, isTiptapContentEmpty } from "../../../common/input/TipTap/TipTap";

export interface ReadonlyAnswerEvidence {
  id: string;
  name: string;
}

// The read-only rendering of a single answer: a yes/no verdict, free text, or nothing answered.
export type ReadonlyAnswer =
  | { kind: "boolean"; value: boolean; comment?: string | null }
  | { kind: "text"; value: string }
  | { kind: "empty" };

interface Props {
  /** Left status bar tone. Buyer views pass a verdict-derived tone; the supplier view uses neutral. */
  accent?: StatusVariant;
  /** Optional leading badge (e.g. the qualification criterion type). */
  badge?: ReactNode;
  title: string;
  /** Small inline label rendered before the rich-text description (e.g. "Documentation requirement"). */
  descriptionLabel?: string;
  descriptionHtml?: string | null;
  /** Label above the answer block. Defaults to the buyer-facing "Supplier's answer". */
  answerLabel?: string;
  answer: ReadonlyAnswer;
  /** Placeholder when nothing was answered. Defaults to the buyer-facing "Not answered by supplier". */
  notAnsweredLabel?: string;
  evidence?: ReadonlyAnswerEvidence[];
  /** Top-right slot — buyer views inject the verdict control here. */
  headerAction?: ReactNode;
  /** Below the answer block — buyer views inject the evaluation-comment field here. */
  footer?: ReactNode;
}

// Shared read-only presentation of a supplier's answer to a qualification or minimum requirement.
// Both the buyer bid-detail tabs and the supplier read-only bid page render through this component,
// so a design change lands in one place. Buyer-only affordances (verdict control, evaluation
// comment) are injected via the headerAction/footer slots rather than baked in here.
export const ReadonlyAnswerCard = ({
  accent = "neutral",
  badge,
  title,
  descriptionLabel,
  descriptionHtml,
  answerLabel,
  answer,
  notAnsweredLabel,
  evidence = [],
  headerAction,
  footer,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px={{ base: "4", md: "6" }}
      py="5"
      position="relative"
      overflow="hidden"
    >
      <StatusAccentBar variant={accent} />
      <Flex align="flex-start" justify="space-between" gap="4" flexWrap="wrap" mb="4">
        <Stack spacing="1.5" minW="0">
          {badge}
          <Heading as="h3" size="sm" color="smText">
            {title}
          </Heading>
          {descriptionHtml && !isTiptapContentEmpty(descriptionHtml) && (
            <Box fontSize="sm" color="smMuted">
              {descriptionLabel && (
                <Text as="span" fontWeight="medium">
                  {descriptionLabel}:
                </Text>
              )}
              <TipTap content={descriptionHtml} editable={false} hasBorder={false} padding="0" maxHeight="" />
            </Box>
          )}
        </Stack>
        {headerAction}
      </Flex>

      <Stack spacing="4">
        <Stack spacing="2">
          <ReadonlyFieldLabel>{answerLabel ?? t("Supplier's answer")}</ReadonlyFieldLabel>
          <AnswerBlock answer={answer} notAnsweredLabel={notAnsweredLabel ?? String(t("Not answered by supplier"))} />
          {evidence.length > 0 && (
            <Stack spacing="1.5">
              {evidence.map((doc) => (
                <HStack
                  key={doc.id}
                  bg="smBackground"
                  border="1px solid"
                  borderColor="smBorder"
                  rounded="md"
                  px="3"
                  py="1.5"
                  spacing="2"
                >
                  <Icon as={FiFile} color="smMuted" />
                  <Text fontSize="sm">{doc.name}</Text>
                </HStack>
              ))}
            </Stack>
          )}
        </Stack>
        {footer}
      </Stack>
    </Box>
  );
};

const AnswerBlock = ({ answer, notAnsweredLabel }: { answer: ReadonlyAnswer; notAnsweredLabel: string }) => {
  const { t } = useTranslation();

  if (answer.kind === "empty") {
    return (
      <Box
        border="1px dashed"
        borderColor="smBorder"
        rounded="lg"
        px="3"
        py="2.5"
        fontSize="sm"
        fontStyle="italic"
        color="smMuted"
      >
        {notAnsweredLabel}
      </Box>
    );
  }

  if (answer.kind === "text") {
    return (
      <Box
        bg="smBackgroundSecondary"
        border="1px solid"
        borderColor="smBorder"
        rounded="lg"
        px="3"
        py="2.5"
        fontSize="sm"
        whiteSpace="pre-wrap"
      >
        {answer.value}
      </Box>
    );
  }

  return (
    <Box bg="smBackgroundSecondary" rounded="lg" px="4" py="3.5">
      <Stack spacing="3" align="flex-start">
        <StatusDotLabel variant={answer.value ? "ok" : "issue"} label={String(answer.value ? t("Yes") : t("No"))} />
        {answer.comment && (
          <Text color="smText" lineHeight="tall" whiteSpace="pre-wrap" maxW="82ch">
            {answer.comment}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export const ReadonlyFieldLabel = ({ children }: { children: ReactNode }) => (
  <Text fontSize="xs" fontWeight="semibold" color="smMuted" textTransform="uppercase" letterSpacing="wide">
    {children}
  </Text>
);
